import { useApplicationSettings } from '@/presentation/contexts/application/application-settings-provider'
import {
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Radio,
  Skeleton,
  Typography
} from '@mui/material'
import React, { Fragment, useEffect } from 'react'
import { i18nTranslationHelper } from '@/i18n'

export type OptionsType = { label: string; value: string; checked: boolean; paramName: string }

interface SearchTypeDecisorProps {
  onSearchTypeChange: (option: OptionsType) => void
  onSearchTypeUpdate: (option: OptionsType) => void
}

export const AllSearchTypeOption = {
  label: i18nTranslationHelper.getTComponents('searchTypeDecisor.searchLabelAll'),
  value: 'search',
  checked: false,
  paramName: 'q'
}

const SearchTypeDecisor: React.FC<SearchTypeDecisorProps> = ({
  onSearchTypeChange,
  onSearchTypeUpdate
}) => {
  const {
    settings: { globalSearch }
  } = useApplicationSettings()

  const t = i18nTranslationHelper.getTComponents

  const [globalSearchOptions, setGlobalSearchOptions] = React.useState<OptionsType[]>([])

  useEffect(() => {
    const { title, forwardUrl, paramName } = globalSearch.route

    if (!forwardUrl || !title || !paramName) {
      setGlobalSearchOptions([])
      onSearchTypeUpdate({ ...AllSearchTypeOption, checked: true, paramName: 'q' })
      return
    }

    const categorySearchOption = {
      label: t('searchTypeDecisor.searchLabelCategory', { category: title }),
      value: forwardUrl,
      checked: true,
      paramName: paramName
    }

    setGlobalSearchOptions([categorySearchOption, AllSearchTypeOption])
    onSearchTypeUpdate(categorySearchOption)
  }, [globalSearch.route?.forwardUrl, globalSearch.route?.title, globalSearch.route?.paramName])

  const handleToggle = (option: OptionsType) => {
    setGlobalSearchOptions(prevState => {
      return prevState.map(item => {
        if (item.value === option.value) {
          return { ...item, checked: true }
        } else {
          return { ...item, checked: false }
        }
      })
    })
    onSearchTypeChange(option as OptionsType)
  }

  if (!globalSearch.enabledSearchType) {
    return null
  }

  return (
    <Fragment>
      {!globalSearchOptions.length
        ? Array.from({ length: 2 }).map((_, index) => (
            <ListItem disablePadding key={`skeleton-${index}`}>
              <ListItemButton disableRipple disabled>
                <ListItemIcon sx={{ minWidth: '40px' }}>
                  <Skeleton animation="wave" width="24px" height="24px" />
                </ListItemIcon>
                <ListItemText primary={<Skeleton animation="wave" height="24px" />} />
              </ListItemButton>
            </ListItem>
          ))
        : globalSearchOptions.map(option => (
            <ListItem disablePadding key={option.value}>
              <ListItemButton disableRipple onClick={() => handleToggle(option)}>
                <ListItemIcon sx={{ minWidth: '40px' }}>
                  <Radio
                    edge="start"
                    size="medium"
                    checked={option.checked}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ 'aria-labelledby': option.label }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body1" color="grey.900">
                      {option.label}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}

      <Divider sx={{ my: theme => theme.spacing(1) }} />
    </Fragment>
  )
}

export default SearchTypeDecisor
